<template>
  <main class="finance-landing">
    <!-- Section 1 — Hero -->
    <section class="finance-hero">
      <div class="container">
        <div class="hero-eyebrow">STRUCTURAL HEALTH — {{ heroDate }} · Updated daily 22:00 UTC</div>
        <h1>Today's structural health</h1>

        <div class="hero-top">
          <div class="hero-stat">
            <span class="tmono">{{ atRiskCount }}</span>
            assets in transition or stress today

            <div v-if="!stressLoading && atRiskPills.length" class="risk-row" aria-label="At-risk assets">
              <button
                v-for="p in atRiskPills"
                :key="p.asset"
                class="dash-pill"
                type="button"
                @click="goLive(p.asset)"
              >
                <span class="dash-ticker">{{ p.label }}</span>
                <span class="dash-dot" :class="p.dotClass"></span>
              </button>
            </div>
          </div>

          <div class="hero-search" role="navigation" aria-label="Search assets">
            <input
              v-model.trim="searchQuery"
              class="asset-search"
              type="search"
              autocomplete="off"
              spellcheck="false"
              placeholder="Search assets (ticker or name)…"
              aria-label="Search assets"
            />

            <div v-if="searchQuery" class="search-results" role="listbox" aria-label="Search results">
              <button
                v-for="a in searchResults"
                :key="a.id"
                class="search-result"
                type="button"
                @click="goLive(a.id)"
              >
                <div class="result-left">
                  <div class="result-ticker">{{ a.label }}</div>
                  <div class="result-name">{{ a.name }}</div>
                </div>
                <div class="result-right">
                  <span class="regime-pill" :class="pillRegimeClass(liveScore(a.id))">
                    {{ pillRegimeText(liveScore(a.id)) }}
                  </span>
                  <span class="result-score">{{ fmtScore(liveScore(a.id)) }}</span>
                </div>
              </button>
              <div v-if="searchResults.length === 0" class="search-empty">No matches.</div>
            </div>

            <div class="popular-row" aria-label="Popular searches">
              <button v-for="id in POPULAR_ASSET_IDS" :key="id" class="pop-pill" type="button" @click="goLive(id)">
                {{ assetLabel(id) }}
              </button>
            </div>
          </div>
        </div>

        <h2 class="section-h2">Pinned</h2>
        <div class="stress-grid" aria-label="Pinned assets">
          <article
            v-for="tile in pinnedTiles"
            :key="tile.key"
            class="card stress-tile"
            :class="tile.toneClass"
            :aria-busy="tile.loading ? 'true' : 'false'"
            role="link"
            tabindex="0"
            @click="tile.loading ? undefined : goLive(tile.asset)"
            @keydown.enter.prevent="tile.loading ? undefined : goLive(tile.asset)"
            @keydown.space.prevent="tile.loading ? undefined : goLive(tile.asset)"
          >
            <div class="tile-top">
              <div class="tile-asset">
                <div class="tile-ticker">{{ tile.label }}</div>
                <div class="tile-name">{{ tile.name }}</div>
              </div>
              <button
                class="tile-share"
                type="button"
                :disabled="tile.loading"
                :title="copiedAsset === tile.asset ? 'Copied' : 'Copy share text'"
                aria-label="Share"
                @click.stop="copyTile(tile)"
              >
                ⧉
              </button>
            </div>

            <div class="tile-score" :class="tile.scoreClass">
              <span v-if="tile.loading" class="tile-skel skel-score" aria-hidden="true"></span>
              <span v-else>{{ tile.scoreText }}</span>
            </div>

            <div class="tile-bottom">
              <div class="regime-pill" :class="tile.regimeClass">
                <span v-if="tile.loading" class="tile-skel skel-pill" aria-hidden="true"></span>
                <span v-else>{{ tile.regimeText }}</span>
              </div>
              <div class="tile-trend">
                <span v-if="tile.loading" class="tile-skel skel-trend" aria-hidden="true"></span>
                <span v-else>{{ tile.trendArrow }} {{ tile.trendText }}</span>
              </div>
            </div>
          </article>
        </div>

        <div class="hero-one-line">Structure breaks before price. SVB: 17 days early. GLD: stress ongoing.</div>

        <div class="hero-cta-row">
          <router-link class="btn-primary hero-live-cta" to="/live">View live scores (all assets) →</router-link>
        </div>
      </div>
    </section>

    <!-- Section 2 — Breaking right now (only if something is breaking) -->
    <section v-if="breakingTiles.length > 0" class="finance-breaking">
      <div class="container">
        <div class="breaking-head">
          <div class="label">BREAKING RIGHT NOW</div>
          <h2>What is breaking right now?</h2>
          <div class="breaking-sub">Assets currently in structural stress (score below 0.55).</div>
        </div>

        <div class="stress-grid" aria-label="Breaking assets">
          <article
            v-for="tile in breakingTiles"
            :key="tile.key"
            class="card stress-tile"
            :class="tile.toneClass"
            :aria-busy="tile.loading ? 'true' : 'false'"
            role="link"
            tabindex="0"
            @click="tile.loading ? undefined : goLive(tile.asset)"
            @keydown.enter.prevent="tile.loading ? undefined : goLive(tile.asset)"
            @keydown.space.prevent="tile.loading ? undefined : goLive(tile.asset)"
          >
            <div class="tile-top">
              <div class="tile-asset">
                <div class="tile-ticker">{{ tile.label }}</div>
                <div class="tile-name">{{ tile.name }}</div>
              </div>
              <button
                class="tile-share"
                type="button"
                :disabled="tile.loading"
                :title="copiedAsset === tile.asset ? 'Copied' : 'Copy share text'"
                aria-label="Share"
                @click.stop="copyTile(tile)"
              >
                ⧉
              </button>
            </div>

            <div class="tile-score" :class="tile.scoreClass">
              <span v-if="tile.loading" class="tile-skel skel-score" aria-hidden="true"></span>
              <span v-else>{{ tile.scoreText }}</span>
            </div>

            <div class="tile-bottom">
              <div class="regime-pill" :class="tile.regimeClass">
                <span v-if="tile.loading" class="tile-skel skel-pill" aria-hidden="true"></span>
                <span v-else>{{ tile.regimeText }}</span>
              </div>
              <div class="tile-trend">
                <span v-if="tile.loading" class="tile-skel skel-trend" aria-hidden="true"></span>
                <span v-else>{{ tile.trendArrow }} {{ tile.trendText }}</span>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- Section 2 — The signal in one sentence -->
    <section class="signal">
      <div class="container">
        <div class="signal-inner">
          <div class="signal-title">Structure breaks before price.</div>
          <div class="signal-sub">
            Structural health measures internal coherence — how an asset behaves relative to the relationships that
            normally support it. When coherence degrades, fragility is building before any chart pattern shows it.
            Mandelbrot proved markets are fractal and turbulent. We measure the coherence of those fractal systems daily.
          </div>
        </div>
      </div>
    </section>

    <!-- Section 4 — Case studies -->
    <section class="case-studies">
      <div class="container">
        <div class="case-grid">
          <article class="card case-card border-transition">
            <div class="case-label">LIVE · UPDATED DAILY</div>
            <div class="case-head">Live example: structural stress can lead price</div>
            <div class="case-body">
              Live structural health updates daily across assets. Use the live view to inspect current stress, regime
              duration, and recent history — then compare with price action and the prevailing narrative.
            </div>
            <div class="case-stat">
              <div class="case-stat-num">Open Live →</div>
              <div class="case-stat-label">see all assets</div>
            </div>
          </article>

          <article class="card case-card border-transition">
            <div class="case-label">FINANCE · BANKING · 2023</div>
            <div class="case-head">17 days of structural warning before Silicon Valley Bank collapsed</div>
            <div class="case-body">
              The structural health signal for the banking sector began declining February 21, 2023 — while SIVB stock
              traded at ~$273 and no public warning existed. FDIC seized the bank March 10, 2023.
            </div>
            <div class="case-stat">
              <div class="case-stat-num">17 days</div>
              <div class="case-stat-label">signal to collapse</div>
            </div>
          </article>

          <article class="card case-card border-navy">
            <div class="case-label">FINANCE · SYSTEMIC · 2008</div>
            <div class="case-head">49 days before the crisis</div>
            <div class="case-body">
              Cross-asset structural coherence began degrading 49 days before Lehman Brothers collapsed. The relationship
              breakdown was visible in coherence metrics long before individual price charts showed the pattern.
            </div>
            <div class="case-stat">
              <div class="case-stat-num">49 days</div>
              <div class="case-stat-label">lead time before Lehman</div>
            </div>
          </article>

          <article class="card case-card border-navy">
            <div class="case-label">EXTERNAL SHOCK · 2020</div>
            <div class="case-head">The system stayed silent before COVID — correctly</div>
            <div class="case-body">
              The 2020 crash was an external shock with no prior structural buildup. The engine produced no pre-failure
              signal — which was the correct output. This validates the difference between structural fragility
              (detectable) and exogenous shocks (not detectable in advance).
            </div>
            <div class="case-stat">
              <div class="case-stat-num">0 false signals</div>
              <div class="case-stat-label">correct silence</div>
            </div>
          </article>
        </div>

        <div class="case-foot">
          Validated on 18 years of SPY data. Not financial advice.
        </div>
      </div>
    </section>

    <!-- Section 6 — How it works -->
    <section class="how">
      <div class="container">
        <div class="cols3">
          <div class="step">
            <div class="step-num">01</div>
            <div class="step-title">Structural coherence</div>
            <div class="step-text">
              Measures cross-asset relationships daily. Not price. Not volatility. The compatibility between an asset and
              the forces that normally support it — the same approach that detected degradation in German power grids 23
              hours before voltage monitoring fired.
            </div>
          </div>
          <div class="step">
            <div class="step-num">02</div>
            <div class="step-title">Divergence window</div>
            <div class="step-text">
              When structure and price move in opposite directions, one must correct. Structure moves first. The
              divergence window is the signal.
            </div>
          </div>
          <div class="step">
            <div class="step-num">03</div>
            <div class="step-title">Plain signal</div>
            <div class="step-text">
              A score (0–1), a regime (stable / transition / stress), and one sentence. No jargon. No noise. The window,
              clearly marked.
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Section 7 — Limitation -->
    <section class="limit">
      <div class="container">
        <div class="card limit-card">
          <div class="label" style="margin-bottom: 12px">WHAT THIS DOESN'T DO</div>
          <p class="limit-text">
            The 2020 COVID crash produced no early structural signal. That was correct — COVID was an external shock with
            no prior internal degradation to detect. This system measures endogenous coherence loss, not sudden exogenous
            events.
            <br /><br />
            Mandelbrot showed markets are fractal — turbulence clusters, tails are fat, and rare events are far more
            common than Gaussian models predict. Classical risk tools assume smoothness. This system monitors coherence
            in fractal systems. It will stay silent when silence is correct.
          </p>
        </div>
      </div>
    </section>

    <!-- Section 7 — Waitlist (coming soon) -->
    <section id="subscribe">
      <div class="container">
        <div class="cta-inner">
          <h2>Waitlist opens soon</h2>
          <p>
            We’re expanding the asset catalog, improving search/navigation, and adding longer history.
            The public waitlist will open soon.
          </p>
          <p style="margin-top: 12px">
            For now, use the live view to explore the current stress map and asset history.
          </p>
          <div style="margin-top: 16px">
            <router-link class="btn-primary" to="/live">View live scores →</router-link>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ASSET_CATALOG, assetLabel, assetName, POPULAR_ASSET_IDS, type AssetMeta } from '../finance/assets'

declare global {
  interface Window {
    CAT_FINANCE_API_BASE?: string
  }
}

type AssetId = string

const router = useRouter()

const stressLoading = ref(true)
const stressRows = ref<
  Array<{
    asset: AssetId
    score: number
    trend7d: number
    name: string
    label: string
    date?: string
  }>
>([])

const searchQuery = ref('')

const PINNED_ALWAYS: AssetId[] = ['SPY', 'BTC-USD']
const PINNED_RANDOM_POOL: AssetId[] = ['AAPL', 'GLD', 'NVDA', 'GOOGL', 'META', 'USO']

function pickRandomPinned(): AssetId[] {
  const pool = [...PINNED_RANDOM_POOL]
  // Fisher–Yates shuffle
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[pool[i], pool[j]] = [pool[j], pool[i]]
  }
  const picked = pool.slice(0, 2)
  return [...PINNED_ALWAYS, ...picked]
}

function initialPinnedIds(): AssetId[] {
  if (typeof window === 'undefined') return pickRandomPinned()

  try {
    const raw = window.sessionStorage.getItem('financePinnedV1')
    if (raw) {
      const parsed = JSON.parse(raw) as unknown
      if (Array.isArray(parsed) && parsed.length === 4 && parsed.includes('SPY') && parsed.includes('BTC-USD')) {
        return parsed.filter((x): x is AssetId => typeof x === 'string')
      }
    }
  } catch {
    // ignore
  }

  const ids = pickRandomPinned()
  try {
    window.sessionStorage.setItem('financePinnedV1', JSON.stringify(ids))
  } catch {
    // ignore
  }
  return ids
}

const pinnedIds = ref<AssetId[]>(initialPinnedIds())

const pinnedList = computed<AssetId[]>(() => {
  const ids = pinnedIds.value
  if (Array.isArray(ids) && ids.length === 4) return ids
  return [...PINNED_ALWAYS, ...PINNED_RANDOM_POOL.slice(0, 2)]
})

function fmtScore(score?: number) {
  if (score === undefined || score === null || Number.isNaN(score)) return '—'
  return score.toFixed(3)
}

const liveByAsset = computed(() => new Map(stressRows.value.map((r) => [r.asset, r])))

function liveScore(asset: AssetId): number | undefined {
  return liveByAsset.value.get(asset)?.score
}

function pillRegimeText(score?: number): string {
  if (score === undefined || score === null || Number.isNaN(score)) return '—'
  return scoreToRegime(score)
}

function pillRegimeClass(score?: number): string {
  if (score === undefined || score === null || Number.isNaN(score)) return 'stable'
  const r = scoreToRegime(score)
  return r === 'STRESS' ? 'stress' : r === 'TRANSITION' ? 'transition' : 'stable'
}

const searchResults = computed<AssetMeta[]>(() => {
  const q = searchQuery.value.trim().toLowerCase()
  if (!q) return []

  return ASSET_CATALOG.filter((a) => {
    const hay = `${a.id} ${a.label} ${a.name}`.toLowerCase()
    return hay.includes(q)
  }).slice(0, 10)
})

const atRiskCount = computed(() => stressRows.value.filter((r) => Number.isFinite(r.score) && r.score < 0.7).length)

const atRiskPills = computed(() => {
  const rows = [...stressRows.value]
    .filter((r) => Number.isFinite(r.score) && r.score < 0.7)
    .sort((a, b) => a.score - b.score)

  return rows.map((r) => ({
    asset: r.asset,
    label: assetLabel(r.asset),
    dotClass: r.score < 0.55 ? 'stress' : 'transition'
  }))
})

const copiedAsset = ref<AssetId | null>(null)
let copiedTimer: number | undefined

const heroDate = computed(() => {
  const d = new Date()
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
})

function apiBase(): string {
  const defaultBase = 'https://api.artrofimov.xyz'
  return (window.CAT_FINANCE_API_BASE || defaultBase).replace(/\/$/, '')
}

function scoreToRegime(score: number): 'STABLE' | 'TRANSITION' | 'STRESS' {
  if (score < 0.55) return 'STRESS'
  if (score < 0.7) return 'TRANSITION'
  return 'STABLE'
}

function toneClass(score: number): 'tone-stable' | 'tone-transition' | 'tone-stress' {
  if (score < 0.55) return 'tone-stress'
  if (score < 0.7) return 'tone-transition'
  return 'tone-stable'
}

function scoreClass(score: number): 'score-stable' | 'score-transition' | 'score-stress' {
  if (score < 0.55) return 'score-stress'
  if (score < 0.7) return 'score-transition'
  return 'score-stable'
}

function fmtTrend(v: number) {
  const sign = v >= 0 ? '+' : ''
  return `${sign}${v.toFixed(3)}`
}

function trendArrow(v: number) {
  if (Math.abs(v) < 0.005) return '→'
  return v > 0 ? '↑' : '↓'
}

function goLive(asset: AssetId) {
  void router.push({ path: '/live', query: { asset } })
}

async function copyTile(tile: { asset?: AssetId; score?: number; label?: string; date?: string; loading?: boolean }) {
  if (tile.loading || !tile.asset || tile.score === undefined) return
  const dateText = tile.date ? new Date(tile.date).toLocaleDateString() : heroDate.value
  const regime = scoreToRegime(tile.score)
  const text = `${tile.label} structural health: ${tile.score.toFixed(2)} (${regime}) — ${dateText} | finance.artrofimov.xyz`

  try {
    await navigator.clipboard.writeText(text)
    copiedAsset.value = tile.asset
    if (copiedTimer) window.clearTimeout(copiedTimer)
    copiedTimer = window.setTimeout(() => {
      copiedAsset.value = null
    }, 1500)
  } catch {
    // ignore
  }
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null
}

function getString(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined
}

function getNumber(v: unknown): number | undefined {
  if (typeof v === 'number') return v
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v)
  return undefined
}

async function fetchStressMap() {
  stressLoading.value = true
  try {
    const res = await fetch(`${apiBase()}/v1/score/live`, { cache: 'no-store' })
    if (!res.ok) throw new Error('stress map fetch failed')
    const raw = (await res.json()) as unknown
    const rowsRaw: unknown[] = Array.isArray(raw)
      ? raw
      : isRecord(raw) && Array.isArray(raw['rows'])
        ? (raw['rows'] as unknown[])
        : []

    const parsed = rowsRaw
      .map((row) => {
        if (!isRecord(row)) return null
        const asset = getString(row['asset'])
        const score = getNumber(row['score'])
        if (!asset || score === undefined) return null
        const trend7d = getNumber(row['trend_7d']) ?? 0
        const date = getString(row['date'])
        return {
          asset,
          score,
          trend7d,
          date,
          name: assetName(asset),
          label: assetLabel(asset)
        }
      })
      .filter((r): r is NonNullable<typeof r> => Boolean(r))

    stressRows.value = parsed
  } catch {
    stressRows.value = []
  } finally {
    stressLoading.value = false
  }
}

const stressTiles = computed(() => {
  const placeholders = pinnedList.value.map((id) => ({
    key: id,
    asset: id,
    label: assetLabel(id),
    name: assetName(id),
    loading: true,
    toneClass: 'tone-stable' as const,
    scoreClass: 'score-stable' as const,
    scoreText: '—',
    regimeClass: 'stable' as const,
    regimeText: '',
    trendArrow: '→',
    trendText: ''
  }))

  if (stressLoading.value || stressRows.value.length === 0) return placeholders

  const by = liveByAsset.value
  return pinnedList.value.map((id) => {
    const row = by.get(id)
    if (!row) {
      return {
        key: id,
        asset: id,
        label: assetLabel(id),
        name: assetName(id),
        loading: true,
        toneClass: 'tone-stable' as const,
        scoreClass: 'score-stable' as const,
        scoreText: '—',
        regimeClass: 'stable' as const,
        regimeText: '',
        trendArrow: '→',
        trendText: ''
      }
    }

    const regime = scoreToRegime(row.score)
    return {
      key: id,
      asset: id,
      label: assetLabel(id),
      name: assetName(id),
      date: row.date,
      loading: false,
      toneClass: toneClass(row.score),
      scoreClass: scoreClass(row.score),
      scoreText: row.score.toFixed(3),
      regimeClass: regime.toLowerCase(),
      regimeText: regime,
      trendArrow: trendArrow(row.trend7d),
      trendText: fmtTrend(row.trend7d),
      score: row.score
    }
  })
})

const pinnedTiles = stressTiles

const breakingTiles = computed(() => {
  const placeholders: Array<{
    key: string
    asset: AssetId
    label: string
    name: string
    loading: boolean
    toneClass: 'tone-stable' | 'tone-transition' | 'tone-stress'
    scoreClass: 'score-stable' | 'score-transition' | 'score-stress'
    scoreText: string
    regimeClass: 'stable' | 'transition' | 'stress'
    regimeText: string
    trendArrow: string
    trendText: string
  }> = []

  if (stressLoading.value) return placeholders
  const stressed = [...stressRows.value].filter((r) => Number.isFinite(r.score) && r.score < 0.55).sort((a, b) => a.score - b.score)
  return stressed.map((row) => {
    const regime = scoreToRegime(row.score)
    return {
      key: row.asset,
      asset: row.asset,
      label: assetLabel(row.asset),
      name: assetName(row.asset),
      date: row.date,
      loading: false,
      toneClass: toneClass(row.score),
      scoreClass: scoreClass(row.score),
      scoreText: row.score.toFixed(3),
      regimeClass: regime.toLowerCase(),
      regimeText: regime,
      trendArrow: trendArrow(row.trend7d),
      trendText: fmtTrend(row.trend7d),
      score: row.score
    }
  })
})

onMounted(async () => {
  await fetchStressMap()
})
</script>

<style scoped>
.tmono {
  font-family: var(--font-mono);
}

.tserif {
  font-family: var(--font-serif);
}

.finance-landing {
  padding-top: 52px; /* fixed nav */
}

.finance-hero {
  padding-top: 100px;
  padding-bottom: 80px;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  position: relative;
  overflow: hidden;
}

.finance-hero::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 400px;
  height: 100%;
  background: linear-gradient(135deg, transparent 60%, rgba(27, 45, 79, 0.03) 100%);
  pointer-events: none;
}


.hero-eyebrow {
  font-size: 10px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--muted);
  margin-bottom: 10px;
}

.finance-hero h1 {
  font-family: var(--font-serif);
  font-size: clamp(40px, 4.4vw, 48px);
  font-weight: 700;
  color: var(--navy);
  letter-spacing: -0.02em;
  line-height: 1.05;
  margin-bottom: 18px;
}

.hero-top {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.hero-stat {
  color: var(--muted);
  font-size: 12px;
}

.hero-search {
  width: min(520px, 100%);
  position: relative;
}

.asset-search {
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 2px;
  padding: 10px 12px;
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--ink);
  background: rgba(255, 255, 255, 0.85);
}

.asset-search:focus {
  outline: none;
  border-color: var(--navy);
}

.search-results {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 8px);
  z-index: 20;
  display: grid;
  gap: 8px;
  padding: 10px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 4px;
  max-height: 420px;
  overflow: auto;
}

.search-result {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.7);
  padding: 10px 12px;
  border-radius: 2px;
  cursor: pointer;
  text-align: left;
}

.search-result:hover {
  border-color: var(--navy);
}

.result-left {
  display: grid;
  gap: 2px;
}

.result-ticker {
  color: var(--navy);
  font-weight: 600;
  font-family: var(--font-mono);
  font-size: 12px;
}

.result-name {
  color: var(--muted);
  font-size: 12px;
}

.result-right {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.result-score {
  color: var(--ink);
  font-family: var(--font-mono);
  font-size: 12px;
  min-width: 56px;
  text-align: right;
}

.search-empty {
  color: var(--muted);
  font-size: 12px;
  padding: 6px 2px;
}

.popular-row {
  margin-top: 8px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.pop-pill {
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.7);
  color: var(--navy);
  font-family: var(--font-mono);
  font-size: 12px;
  padding: 8px 10px;
  border-radius: 999px;
  cursor: pointer;
}

.pop-pill:hover {
  border-color: var(--navy);
}

.dash-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.7);
  padding: 8px 10px;
  border-radius: 2px;
  cursor: pointer;
}

.dash-ticker {
  color: var(--navy);
  font-weight: 600;
  font-family: var(--font-mono);
  font-size: 11px;
}

.dash-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  display: inline-block;
}

.dash-dot.stable {
  background: var(--stable);
}

.dash-dot.transition {
  background: var(--transition-c);
}

.dash-dot.stress {
  background: var(--signal);
}

.section-h2 {
  margin-top: 14px;
  margin-bottom: 10px;
  font-family: var(--font-serif);
  font-size: 20px;
  color: var(--navy);
}

.finance-breaking {
  padding: 70px 0;
  border-bottom: 1px solid var(--border);
  background: #fff;
}

.breaking-head {
  margin-bottom: 14px;
}

.finance-breaking h2 {
  font-family: var(--font-serif);
  font-size: 32px;
  color: var(--navy);
  letter-spacing: -0.02em;
  margin-top: 8px;
}

.breaking-sub {
  margin-top: 8px;
  color: var(--muted);
  font-size: 12px;
}

.stress-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.stress-tile {
  padding: 16px;
  border-left: 4px solid var(--border);
  cursor: pointer;
}

.stress-tile.tone-stable {
  background: var(--surface);
  border-left-color: var(--stable);
}

.stress-tile.tone-transition {
  background: var(--surface);
  border-left-color: var(--transition-c);
}

.stress-tile.tone-stress {
  background: var(--surface);
  border-left-color: var(--signal);
}

@supports (background: color-mix(in srgb, red 10%, white)) {
  .stress-tile.tone-stable {
    background: color-mix(in srgb, var(--stable) 6%, var(--surface));
  }

  .stress-tile.tone-transition {
    background: color-mix(in srgb, var(--transition-c) 6%, var(--surface));
  }

  .stress-tile.tone-stress {
    background: color-mix(in srgb, var(--signal) 7%, var(--surface));
  }
}

.tile-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
}

.tile-ticker {
  font-family: var(--font-mono);
  font-size: 13px;
  letter-spacing: 0.08em;
  color: var(--navy);
}

.tile-name {
  margin-top: 4px;
  font-size: 12px;
  color: var(--muted);
}

.tile-share {
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.65);
  border-radius: 2px;
  cursor: pointer;
  color: var(--muted);
  font-size: 14px;
  line-height: 1;
}

.tile-share:hover {
  border-color: var(--navy);
  color: var(--navy);
}

.tile-share:disabled {
  opacity: 0.6;
  cursor: default;
}

.tile-score {
  margin-top: 14px;
  font-family: var(--font-mono);
  font-size: 28px;
  font-variant-numeric: tabular-nums;
}

.tile-score.score-stable {
  color: var(--stable);
}

.tile-score.score-transition {
  color: var(--transition-c);
}

.tile-score.score-stress {
  color: var(--signal);
}

.tile-bottom {
  margin-top: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

.regime-pill {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid transparent;
}

.regime-pill.stable {
  background: rgba(42, 107, 74, 0.15);
  border-color: rgba(42, 107, 74, 0.25);
  color: var(--stable);
}

.regime-pill.transition {
  background: rgba(122, 79, 0, 0.15);
  border-color: rgba(122, 79, 0, 0.25);
  color: var(--transition-c);
}

.regime-pill.stress {
  background: rgba(184, 48, 24, 0.15);
  border-color: rgba(184, 48, 24, 0.25);
  color: var(--signal);
}

.tile-trend {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--navy);
  letter-spacing: 0.02em;
  font-variant-numeric: tabular-nums;
}

.tile-skel {
  display: inline-block;
  background-color: var(--grid-line);
  background-image: linear-gradient(90deg, var(--grid-line) 0%, var(--bg) 50%, var(--grid-line) 100%);
  background-size: 200% 100%;
  border-radius: 2px;
  animation: tile-pulse 1.15s ease-in-out infinite;
}

@keyframes tile-pulse {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tile-skel {
    animation: none;
  }
}

.skel-score {
  width: 110px;
  height: 28px;
}

.skel-pill {
  width: 92px;
  height: 14px;
}

.skel-trend {
  width: 86px;
  height: 14px;
}

.hero-one-line {
  margin-top: 14px;
  font-size: 12px;
  color: var(--muted);
  text-align: left;
}

.hero-cta-row {
  margin-top: 14px;
}


.risk-row {
  margin-top: 10px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.hero-live-cta {
  width: 100%;
  text-align: center;
}

.signal-inner {
  max-width: 760px;
  margin: 0 auto;
  text-align: center;
}

.signal-title {
  font-family: var(--font-serif);
  font-size: clamp(36px, 4vw, 42px);
  font-weight: 700;
  color: var(--navy);
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.signal-sub {
  margin-top: 14px;
  font-size: 13px;
  color: var(--muted);
  line-height: 1.8;
}

.case-studies {
  padding: 80px 0;
  background: var(--bg);
  background-image: linear-gradient(var(--grid-line) 1px, transparent 1px),
    linear-gradient(90deg, var(--grid-line) 1px, transparent 1px);
  background-size: 40px 40px;
  border-bottom: 1px solid var(--border);
}

.case-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.case-card {
  padding: 22px;
  border-left-width: 4px;
  display: flex;
  flex-direction: column;
  min-height: 360px;
}

.border-signal {
  border-left-color: var(--signal);
}

.border-transition {
  border-left-color: var(--transition-c);
}

.border-navy {
  border-left-color: var(--navy);
}

.border-stable {
  border-left: 4px solid var(--stable);
}

.case-label {
  font-size: 10px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--muted);
  margin-bottom: 10px;
}

.case-head {
  font-family: var(--font-serif);
  font-size: 18px;
  line-height: 1.2;
  color: var(--navy);
  margin-bottom: 10px;
}

.case-body {
  font-size: 12px;
  color: var(--muted);
  line-height: 1.7;
  flex: 1 1 auto;
}

.case-stat {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid var(--border);
}

.case-stat-num {
  font-family: var(--font-mono);
  font-size: 18px;
  color: var(--navy);
  font-variant-numeric: tabular-nums;
}

.case-stat-label {
  margin-top: 2px;
  font-size: 10px;
  color: var(--muted);
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.case-foot {
  margin-top: 18px;
  text-align: center;
  font-size: 11px;
  color: var(--muted);
}

.what-you-get {
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  padding: 80px 0;
}

.get-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
}

.get-card {
  padding: 22px;
  display: flex;
  flex-direction: column;
}

.get-card .list {
  flex: 1 1 auto;
}

.get-title {
  font-family: var(--font-serif);
  font-size: 20px;
  color: var(--navy);
  margin-bottom: 6px;
}

.get-sub {
  font-size: 12px;
  color: var(--muted);
  margin-bottom: 14px;
}

.get-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.pill {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: rgba(27, 45, 79, 0.06);
  color: var(--navy);
  font-size: 10px;
  letter-spacing: 0.12em;
}

.get-note {
  margin-top: 14px;
  text-align: center;
  font-size: 11px;
  color: var(--muted);
}

.how {
  padding: 80px 0;
  background: var(--bg);
  background-image: linear-gradient(var(--grid-line) 1px, transparent 1px),
    linear-gradient(90deg, var(--grid-line) 1px, transparent 1px);
  background-size: 40px 40px;
  border-bottom: 1px solid var(--border);
}

.limit {
  padding: 80px 0;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
}

.limit-card {
  padding: 22px;
  max-width: 760px;
  margin: 0 auto;
  text-align: center;
}

.limit-text {
  font-size: 14px;
  color: var(--muted);
  line-height: 1.8;
}

#subscribe {
  padding: 80px 0;
  background: var(--navy);
  position: relative;
  overflow: hidden;
}

#subscribe::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: 40px 40px;
}

@media (max-width: 720px) {
  .stress-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .case-grid {
    grid-template-columns: 1fr;
  }

  .get-grid {
    grid-template-columns: 1fr;
  }
}
</style>
